import { createSelector } from 'reselect';

import { RootState } from '../../reducers';
import { getMixersList } from '../mixers/mixers.selector';
import { getFSAdvancedSettings, getSettings } from '../settings/settings.selector';

export const getScaleTypeList = (state: RootState) => state.entities.scaleTypes;

export const getScaleTypeById = createSelector(
  [getScaleTypeList, (state: RootState, id: string): string => id],
  (scaleTypeList, id) => scaleTypeList.find((scaleType) => scaleType.id === id),
);

export const getUsedScaleTypes = createSelector([getScaleTypeList, getMixersList], (scaleTypes, mixers) => {
  const usedScaleTypeIds = mixers.map((mixer) => mixer.scaleTypeId);
  return scaleTypes.filter((scaleType) => usedScaleTypeIds.includes(scaleType.id));
});

export const getIsSixCharDisplay = createSelector([getUsedScaleTypes], (scaleTypes) =>
  scaleTypes.map((scaleType) => scaleType.sixCharDisplay).includes(true),
);

export const isListModeAvailable = createSelector([getMixersList], (mixers): boolean =>
  mixers.every((mixer) => mixer.supportsListMode),
);

export const getIsAllUsedBluetooth = createSelector([getUsedScaleTypes], (scaleTypes) =>
  scaleTypes.every((scaleType) => scaleType.bluetooth),
);

export const hasMixerWithFeedingExternalSystem = createSelector(
  [getUsedScaleTypes, getFSAdvancedSettings],
  (scaleTypes, fsAdvancedSettings) => fsAdvancedSettings && scaleTypes.some((scaleType) => scaleType.feedingSystem),
);

export const isAllMixersWithFeedingExternalSystem = createSelector([getUsedScaleTypes], (scaleTypes) =>
  scaleTypes.every((scaleType) => scaleType.feedingSystem),
);

export const hasOperatorMangementMixer = createSelector([getUsedScaleTypes], (scaleTypes) =>
  scaleTypes.some((scaleType) => scaleType.operatorManagement),
);

export const hasBinControlMixer = createSelector([getUsedScaleTypes], (scaleTypes) =>
  scaleTypes.some((scaleType) => scaleType.binControl),
);

export const canCreateFeedingPeriod = createSelector(
  [getUsedScaleTypes, getSettings],
  (scaleTypes, settings) => settings.unlimitedFeedings && scaleTypes.some((scaleType) => scaleType.unlimitedFeedings),
);
